import axios from "axios";
import * as Sentry from "@sentry/react";

const rawBase = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
// Ensure the base URL ends with /api — the backend mounts all routes under /api.
const baseURL = rawBase.replace(/\/+$/, '').replace(/\/api$/, '') + '/api';

const api = axios.create({
  baseURL,
  withCredentials: true,
});

const FEATURE_TAGS = [
  ['/auth', 'auth'],
  ['/submissions', 'submissions'],
  ['/notifications', 'notifications'],
  ['/competition', 'competition'],
  ['/teams', 'team'],
  ['/judging', 'judging'],
  ['/admin', 'admin'],
  ['/waitlist', 'waitlist'],
];

function tagFeatureForPath(path) {
  for (const [prefix, tag] of FEATURE_TAGS) {
    if (path === prefix || path.startsWith(prefix + '/')) {
      Sentry.setTag('feature', tag);
      return;
    }
  }
}

// Attach access token to requests
api.interceptors.request.use(
  (config) => {
    const path = (config.url || '').replace(/^\/?/, '/');
    tagFeatureForPath(path);
    const token = localStorage.getItem('hdl_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 — attempt token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// 401s from these endpoints are expected auth failures (bad credentials, expired
// reset code, etc.) — NOT an expired session. They must propagate to the calling
// page so it can show the real error, instead of triggering a refresh + redirect.
const NO_REFRESH_PATHS = [
  '/auth/login',
  '/auth/refresh-token',
  '/auth/register',
  '/auth/verify-otp',
  '/auth/resend-otp',
  '/auth/forgot-password',
  '/auth/reset-password',
];

const isAuthEndpoint = (url = '') => NO_REFRESH_PATHS.some(p => url.includes(p));

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint(originalRequest?.url)) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(`${baseURL}/auth/refresh-token`, {}, { withCredentials: true });
        const newToken = data.data.accessToken;
        localStorage.setItem('hdl_token', newToken);
        api.defaults.headers.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('hdl_token');
        localStorage.removeItem('hdl_user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Capture 5xx + network/CORS/timeout errors. Skip non-401 4xx (validation, auth state)
    // because those are expected and would create noise.
    const status = error.response?.status;
    if (!status || status >= 500) {
      Sentry.captureException(error, {
        tags: { http_status: status || 'network_error' },
        extra: { url: originalRequest?.url, method: originalRequest?.method },
      });
    }

    return Promise.reject(error);
  }
);

export default api;
